import type { Product } from "schemas/generated/product";

import * as React from "react";

import Button from "@replo/design-system/components/button/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import twMerge from "@replo/design-system/utils/twMerge";
import { ImageIcon, Palette } from "lucide-react";

import { mediaToImageUrls } from "./utils";

type ProductOptionValue = NonNullable<
  Product["options"]
>[number]["optionValues"][number];
type ProductOption = NonNullable<Product["options"]>[number];

interface ProductOptionsEditorProps {
  product: Product;
  onThumbnailClick: (params: {
    optionName: string;
    optionValue: string;
    optionValueId: string;
    currentColor: string | null;
    currentImage: string | null;
  }) => void;
  onImageSetClick: (params: {
    optionName: string;
    optionValue: string;
    optionValueId: string;
    currentAssetIds: string[];
  }) => void;
  initialSelectedOptionId?: string;
}

const MAX_PREVIEW_IMAGES = 3;

export function ProductsOptionsEditor({
  product,
  onThumbnailClick,
  onImageSetClick,
  initialSelectedOptionId,
}: ProductOptionsEditorProps) {
  const [selectedOptionId, setSelectedOptionId] = React.useState<string | null>(
    initialSelectedOptionId ?? product.options?.[0]?.id ?? null,
  );

  const selectedOption = React.useMemo(
    () => product.options?.find((opt) => opt.id === selectedOptionId) ?? null,
    [selectedOptionId, product.options],
  );

  const ImageSetPreview = ({
    optionValue,
  }: {
    optionValue: ProductOptionValue;
  }) => {
    const imageUrls = mediaToImageUrls(optionValue.media);

    if (imageUrls.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">
          No images in swatch
        </span>
      );
    }

    const previewImages = imageUrls.slice(0, MAX_PREVIEW_IMAGES);
    const remainingCount = imageUrls.length - MAX_PREVIEW_IMAGES;

    return (
      <div className="flex items-center gap-2">
        <div className="flex -space-x-1">
          {previewImages.map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Image ${index + 1}`}
              className="h-12 w-12 rounded border border-border-subtle bg-white object-cover"
              style={{ zIndex: previewImages.length - index }}
            />
          ))}
          {remainingCount > 0 && (
            <div
              className="flex h-12 w-12 items-center justify-center rounded border border-border-subtle bg-surface-subtle text-xs font-medium text-foreground-muted"
              style={{ zIndex: 0 }}
            >
              +{remainingCount}
            </div>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {imageUrls.length} {imageUrls.length === 1 ? "image" : "images"}
        </span>
      </div>
    );
  };

  const ThumbnailElement = ({
    optionValue,
  }: {
    optionValue: ProductOptionValue;
  }) => {
    if (optionValue.color) {
      return (
        <div
          className="h-16 w-16 rounded border border-border flex-shrink-0"
          style={{ backgroundColor: optionValue.color }}
          title={optionValue.color}
        />
      );
    }

    if (optionValue.image) {
      return (
        <img
          src={optionValue.image}
          alt={`${optionValue.value} thumbnail`}
          className="h-16 w-16 rounded object-cover border border-border flex-shrink-0"
        />
      );
    }

    return (
      <div className="flex h-16 w-16 items-center justify-center rounded border border-border bg-muted flex-shrink-0">
        <ImageIcon size={24} className="text-muted-foreground" />
      </div>
    );
  };

  const getThumbnailType = (optionValue: ProductOptionValue) => {
    if (optionValue.color) {
      return "Color";
    }
    if (optionValue.image) {
      return "Image";
    }
    return "None";
  };

  const handleThumbnailClick = (
    option: ProductOption,
    optionValue: ProductOptionValue,
  ) => {
    onThumbnailClick({
      optionName: option.title,
      optionValue: optionValue.value ?? "",
      optionValueId: optionValue.id,
      currentColor: optionValue.color,
      currentImage: optionValue.image,
    });
  };

  const handleImageSetClick = (
    option: ProductOption,
    optionValue: ProductOptionValue,
  ) => {
    const currentAssetIds = optionValue.media?.map((media) => media.id) ?? [];

    onImageSetClick({
      optionName: option.title,
      optionValue: optionValue.value ?? "",
      optionValueId: optionValue.id,
      currentAssetIds,
    });
  };

  const StatusText = ({ option }: { option: ProductOption }) => {
    const configuredCount =
      option.optionValues?.filter((ov) => ov.color || ov.image).length ?? 0;
    const totalCount = option.optionValues?.length ?? 0;
    const allConfigured = configuredCount === totalCount && totalCount > 0;

    return (
      <span className="text-sm font-medium text-muted-foreground">
        {allConfigured
          ? `All ${option.title.toLowerCase()} configured`
          : `${configuredCount} of ${totalCount} ${option.title.toLowerCase()} values configured`}
      </span>
    );
  };

  const OptionValueCard = ({
    option,
    optionValue,
  }: {
    option: ProductOption;
    optionValue: ProductOptionValue;
  }) => {
    const imageCount = optionValue.media?.length ?? 0;

    return (
      <Card className="border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <ThumbnailElement optionValue={optionValue} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-base text-foreground-default">
                {optionValue.value}
              </p>
              <p className="text-xs text-muted-foreground">
                Thumbnail: {getThumbnailType(optionValue)}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground-default">
              Image Swatch
            </p>
            <ImageSetPreview optionValue={optionValue} />
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              layoutClassName="flex-1 h-8"
              startEnhancer={<Palette size={14} />}
              onClick={() => handleThumbnailClick(option, optionValue)}
            >
              Edit Thumbnail
            </Button>
            <Button
              variant="secondary"
              size="sm"
              layoutClassName="flex-1 h-8"
              startEnhancer={<ImageIcon size={14} />}
              onClick={() => handleImageSetClick(option, optionValue)}
            >
              {imageCount > 0 ? "Edit" : "Add"} Images
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!product.options?.length) {
    return (
      <p className="text-foreground-muted py-4 text-center">
        This product has no options defined.
      </p>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-1">
        <CardTitle className="text-lg">
          {selectedOption?.title ?? "Option"} Configuration
        </CardTitle>
        <p className="text-sm text-muted mt-1">
          Configure thumbnails and image swatches for option values
        </p>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        <div className="flex flex-wrap gap-2">
          {product.options.map((option) => {
            const totalCount = option.optionValues?.length ?? 0;
            const isSelected = selectedOptionId === option.id;

            return (
              <button
                key={option.id}
                onClick={() =>
                  setSelectedOptionId(isSelected ? null : option.id)
                }
                className={twMerge(
                  "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                  isSelected
                    ? "bg-secondary text-primary-foreground border-secondary"
                    : "bg-background text-foreground border-border hover:bg-accent hover:text-accent-foreground",
                )}
              >
                {option.title} ({totalCount})
              </button>
            );
          })}
        </div>

        {selectedOption && <StatusText option={selectedOption} />}

        {selectedOption?.optionValues && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedOption.optionValues.map((optionValue) => (
              <OptionValueCard
                key={optionValue.id}
                option={selectedOption}
                optionValue={optionValue}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
